import { useQuery } from "@tanstack/react-query";

import { deletedByLabel } from "../lib/chat";
import { formatDate } from "../lib/format";
import { useTRPC } from "../lib/trpc";
import { ChatMuteButton, ChatUnmuteButton } from "./ChatMuteControl";
import { FactGroup } from "./Facts";

/** How many of the user's latest messages the card lists; the Chat page has the rest. */
const RECENT = 10;

/** A shopper's standing in storefront chat: their mute, the control that changes it, their latest messages. */
export function UserChatCard({
  endUserId,
  username,
}: {
  endUserId: string;
  username: string | null;
}) {
  const trpc = useTRPC();
  const query = useQuery(trpc.chat.userMessages.queryOptions({ endUserId, limit: RECENT }));
  const mute = query.data?.mute ?? null;
  const messages = query.data?.items ?? [];

  return (
    <FactGroup
      title="Chat"
      action={
        query.data &&
        (mute ? (
          <ChatUnmuteButton endUserId={endUserId} />
        ) : (
          <ChatMuteButton endUserId={endUserId} name={username} />
        ))
      }
    >
      {query.error && <p className="text-sm text-destructive">{query.error.message}</p>}
      {query.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {query.data && (
        <div className="flex flex-col gap-3 text-sm">
          <p className="text-muted-foreground">
            {mute
              ? `Muted ${mute.expiresAt ? `until ${formatDate(mute.expiresAt)}` : "until unmuted"}${mute.reason ? `: ${mute.reason}` : "."}`
              : username
                ? "Can chat."
                : "Has no username yet, so cannot chat."}
          </p>
          {messages.length === 0 ? (
            <p className="text-muted-foreground">No messages.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {messages.map((message) => (
                <li key={message.messageId} className="min-w-0">
                  <span className="text-xs text-muted-foreground">
                    {formatDate(message.createdAt)}
                  </span>
                  <p className="break-words">
                    {message.deletedAt ? (
                      <span className="text-muted-foreground italic">
                        {deletedByLabel(message.deletedBy)}
                      </span>
                    ) : (
                      message.text
                    )}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </FactGroup>
  );
}
